// ============================================================================
// ALARM CLOCK
// ============================================================================

import CONFIG from '../config.js';
import { DetachedClockHand } from './DetachedClockHand.js';
import { BrokenClock } from './BrokenClock.js';
import { BreakableEntity } from './BreakableEntity.js';

export class AlarmClock extends BreakableEntity {
    constructor(x, y, physics = null, parent = null) {
        // AlarmClock now includes SideTable - use SideTable dimensions
        super(x, y, 40, 40, physics); // SideTable size
        this.clockWidth = 24;
        this.clockHeight = 20;
        this.isOn = false;
        this.isAnimating = false;
        this.animationTime = 0;
        this.animationDuration = 0;
        this.wobbleTime = 0;
        this.wobbleAmplitude = 0;
        this.alarmTimer = CONFIG.alarmClockInterval;
        this.heightOffset = 20; // For side table rendering
        
        // Override sensor: position 50 pixels above center (at clock level)
        if (physics && this.sensorBody) {
            physics.removeBody(this.sensorBody);
            this.sensorBody = null;
        }
        if (physics) {
            const sensorRadius = (CONFIG.interactionRadius || 50) * 0.8;
            // Position sensor 50 pixels above center (where the clock is)
            this.sensorBody = physics.createSensor(x, y - 50, sensorRadius);
            physics.linkEntity(this, this.sensorBody);
        }
        
        // Create physics body for side table (obstacle)
        if (physics) {
            // Update the body to match side table size
            if (this.body) {
                physics.removeBody(this.body);
            }
            this.body = physics.createRectangle(x, y, this.width, this.height, {
                isStatic: true,
                friction: 0,
                restitution: 0
            });
            physics.linkEntity(this, this.body);
        }
    }

    canInteract() {
        return !this.isAnimating && this.isOn && !this.isBroken;
    }

    performAction(level, aggression, game) {
        this.isOn = false;
        this.isAnimating = true;
        this.animationTime = 0;
        this.animationDuration = CONFIG.doorOpenSpeed[level];
        this.alarmTimer = CONFIG.alarmClockInterval;

        if (level >= 1) {
            this.wobbleAmplitude = level * 2;
            this.wobbleTime = 0.5;
        }
    }

    getBreakThreshold() {
        return CONFIG.alarmClockBreakThreshold;
    }

    onBreak(game) {
        this.isOn = false;
        
        // Spawn broken clock (the alarm clock becomes a broken clock)
        if (!game.brokenClocks) game.brokenClocks = [];
        // Position broken clock on top of side table
        const clockY = this.y - (this.height / 2) - this.heightOffset - this.clockHeight / 2 - 2;
        game.brokenClocks.push(new BrokenClock(
            this.x,
            clockY,
            this.clockWidth,
            this.clockHeight,
            this.damage,
            this.physics
        ));
        
        game.detachedClockHands.push(new DetachedClockHand(this.x, clockY, true));
        game.detachedClockHands.push(new DetachedClockHand(this.x, clockY, false));
        
        // Side table remains (damaged) - we'll draw it in the draw method
    }

    update(dt) {
        if (this.isBroken) return;

        // Sync position from physics body
        if (this.body && this.physics) {
            this.x = this.body.position.x;
            this.y = this.body.position.y;
        }
        
        // Update sensor position to stay 50 pixels above center (at clock level)
        if (this.sensorBody && this.physics) {
            this.physics.setPosition(this.sensorBody, this.x, this.y - 50);
        }

        this.alarmTimer -= dt;
        if (this.alarmTimer <= 0 && !this.isOn) {
            this.isOn = true;
            this.alarmTimer = CONFIG.alarmClockInterval;
        }

        if (this.isAnimating) {
            this.animationTime += dt;
            const progress = Math.min(this.animationTime / this.animationDuration, 1);
            if (progress >= 1) this.isAnimating = false;
        }

        if (this.wobbleTime > 0) this.wobbleTime -= dt;
    }

    draw(ctx, camera) {
        ctx.save();
        let wobbleX = 0;
        let wobbleY = 0;
        if (this.wobbleTime > 0 && !this.isBroken) {
            wobbleX = Math.sin(this.wobbleTime * 30) * this.wobbleAmplitude * (this.wobbleTime / 0.5);
            wobbleY = Math.cos(this.wobbleTime * 30) * this.wobbleAmplitude * (this.wobbleTime / 0.5);
        }

        const drawX = this.x + wobbleX;
        const drawY = this.y + wobbleY;
        
        // Draw Side Table (always visible, damaged if broken)
        this.drawSideTable(ctx, drawX, drawY);
        
        // Draw Alarm Clock on top (only if not broken)
        if (!this.isBroken) {
            this.drawClock(ctx, drawX, drawY, wobbleX, wobbleY);
        }
        
        ctx.restore();
    }
    
    drawSideTable(ctx, drawX, drawY) {
        // --- 3/4 PERSPECTIVE SETTINGS ---
        const depth = 6;
        const heightOffset = this.heightOffset;

        // Side table colors - darker if damaged/broken
        let tableColor = CONFIG.colors.sideTable;
        if (this.isBroken || this.damage > 0) {
            // Damaged side table - darker/more damaged
            if (this.damage >= CONFIG.alarmClockDamageThresholds?.[2]) {
                tableColor = '#2E1B14';
            } else if (this.damage >= CONFIG.alarmClockDamageThresholds?.[1]) {
                tableColor = '#3E2723';
            } else if (this.damage >= CONFIG.alarmClockDamageThresholds?.[0]) {
                tableColor = '#4E342E';
            }
        }
        
        const darkerColor = '#3E2723';
        const topColor = this.isBroken ? '#4E342E' : '#5D4037';

        // 1. Draw SIDE/THICKNESS
        ctx.fillStyle = darkerColor;
        ctx.fillRect(drawX - this.width / 2, drawY - this.height / 2 - heightOffset, this.width, this.height + heightOffset);

        // 2. Draw TOP SURFACE
        ctx.fillStyle = topColor;
        ctx.fillRect(drawX - this.width / 2, drawY - this.height / 2 - heightOffset, this.width, depth);
        ctx.strokeStyle = '#000';
        ctx.strokeRect(drawX - this.width / 2, drawY - this.height / 2 - heightOffset, this.width, depth);

        // 3. Draw FRONT FACE
        const frontY = drawY - this.height / 2 - heightOffset + depth;
        const frontHeight = this.height + heightOffset - depth;

        ctx.fillStyle = tableColor;
        ctx.fillRect(drawX - this.width / 2, frontY, this.width, frontHeight);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
        ctx.strokeRect(drawX - this.width / 2, frontY, this.width, frontHeight);

        // Table legs (on front face, at corners)
        ctx.fillStyle = darkerColor;
        const legSize = 4;
        ctx.fillRect(drawX - this.width / 2, frontY, legSize, legSize);
        ctx.fillRect(drawX + this.width / 2 - legSize, frontY, legSize, legSize);
        ctx.fillRect(drawX - this.width / 2, frontY + frontHeight - legSize, legSize, legSize);
        ctx.fillRect(drawX + this.width / 2 - legSize, frontY + frontHeight - legSize, legSize, legSize);
        
        // Damage cracks on side table
        if (this.isBroken || this.damage >= CONFIG.alarmClockDamageThresholds?.[0]) {
            ctx.strokeStyle = '#000';
            ctx.lineWidth = 1;
            ctx.globalAlpha = 0.5;
            const numCracks = Math.min(Math.floor(this.damage / 2) || 2, 6);
            for (let i = 0; i < numCracks; i++) {
                const x1 = drawX + (Math.random() - 0.5) * this.width;
                const y1 = frontY + (Math.random() - 0.5) * frontHeight;
                ctx.beginPath(); ctx.moveTo(x1, y1);
                ctx.lineTo(x1 + (Math.random() - 0.5) * 15, y1 + (Math.random() - 0.5) * 15);
                ctx.stroke();
            }
            ctx.globalAlpha = 1;
        }
    }
    
    drawClock(ctx, drawX, drawY, wobbleX, wobbleY) {
        // Position clock on top of side table
        const clockX = drawX + wobbleX;
        const clockY = drawY - (this.height / 2) - this.heightOffset - this.clockHeight / 2 - 2 + wobbleY;

        // --- 3/4 PERSPECTIVE SETTINGS ---
        const depth = 4;
        const heightOffset = 12;

        let clockColor = CONFIG.colors.alarmClock || '#E0E0E0';
        if (this.damage >= CONFIG.alarmClockDamageThresholds?.[2]) clockColor = '#4B2010';
        else if (this.damage >= CONFIG.alarmClockDamageThresholds?.[1]) clockColor = '#6B3410';
        else if (this.damage >= CONFIG.alarmClockDamageThresholds?.[0]) clockColor = '#8B4010';

        const darkerColor = '#B0B0B0';
        const topColor = '#F0F0F0';

        // 1. Draw SIDE/THICKNESS
        ctx.fillStyle = darkerColor;
        ctx.fillRect(clockX - this.clockWidth / 2, clockY - this.clockHeight / 2 - heightOffset, this.clockWidth, this.clockHeight + heightOffset);

        // 2. Draw TOP SURFACE
        ctx.fillStyle = topColor;
        ctx.fillRect(clockX - this.clockWidth / 2, clockY - this.clockHeight / 2 - heightOffset, this.clockWidth, depth);
        ctx.strokeStyle = '#000';
        ctx.strokeRect(clockX - this.clockWidth / 2, clockY - this.clockHeight / 2 - heightOffset, this.clockWidth, depth);

        // 3. Draw FRONT FACE
        const frontY = clockY - this.clockHeight / 2 - heightOffset + depth;
        const frontHeight = this.clockHeight + heightOffset - depth;

        ctx.fillStyle = clockColor;
        ctx.fillRect(clockX - this.clockWidth / 2, frontY, this.clockWidth, frontHeight);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
        ctx.strokeRect(clockX - this.clockWidth / 2, frontY, this.clockWidth, frontHeight);

        // Face (on front)
        ctx.fillStyle = '#FFF';
        ctx.fillRect(clockX - this.clockWidth / 2 + 1, frontY + 1, this.clockWidth - 2, frontHeight - 2);
        
        // Hands
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(clockX, clockY); ctx.lineTo(clockX, clockY - 4);
        ctx.moveTo(clockX, clockY); ctx.lineTo(clockX + 4, clockY);
        ctx.stroke();

        // Numerals
        ctx.fillStyle = '#000';
        ctx.beginPath();
        ctx.arc(clockX, clockY - 6, 1, 0, Math.PI * 2);
        ctx.arc(clockX + 6, clockY, 1, 0, Math.PI * 2);
        ctx.arc(clockX, clockY + 6, 1, 0, Math.PI * 2);
        ctx.arc(clockX - 6, clockY, 1, 0, Math.PI * 2);
        ctx.fill();

        if (this.isOn) {
            const pulse = Math.sin(Date.now() / 100) * 0.5 + 0.5;
            ctx.fillStyle = `rgba(255, 0, 0, ${pulse})`;
            ctx.beginPath();
            ctx.arc(clockX + this.clockWidth / 2 - 4, frontY + 4, 3, 0, Math.PI * 2);
            ctx.fill();
        }

        // Damage cracks
        if (this.damage >= CONFIG.alarmClockDamageThresholds?.[0]) {
            ctx.strokeStyle = '#000';
            ctx.lineWidth = 0.5;
            ctx.globalAlpha = 0.5;
            const numCracks = Math.min(Math.floor(this.damage / 2), 4);
            for (let i = 0; i < numCracks; i++) {
                const x1 = clockX + (Math.random() - 0.5) * this.clockWidth;
                const y1 = frontY + (Math.random() - 0.5) * frontHeight;
                ctx.beginPath(); ctx.moveTo(x1, y1);
                ctx.lineTo(x1 + (Math.random() - 0.5) * 8, y1 + (Math.random() - 0.5) * 8);
                ctx.stroke();
            }
            ctx.globalAlpha = 1;
        }
    }
}
